/**
 * Browser-side memory store for Statsig-resolved locale config.
 *
 * Stores the full `StatsigLocaleDynamicConfig` (not just feature-gate booleans)
 * so non-React code can read locale-specific values like `trustArcDomain`,
 * `trustArcCookieName`, `additionalFbPixelsAllowed` etc. from Statsig.
 *
 * Country-compliance (UK/GDPR) is read straight from the `countryCompliant` key
 * of this config — use `getIsCountryCompliant()` below. There is no separate
 * `isCountryCompliant` feature-gate.
 *
 * React components should use `useStatsigDynamicLocaleConfig()`.
 */

import { LOCALE_COOKIE } from '@src/config/localisation/locale-config.static-constants'
import { getLocaleConfig } from '@src/config/localisation/statsig-locale-config-constants'
import type { StatsigLocaleDynamicConfig } from '@src/config/localisation/statsig-locale-config.types'
import getCookie from '@src/quince-site-common/src/utils/getCookie'

const isMergeableObject = (item: unknown): item is Record<string, unknown> =>
  typeof item === 'object' && item !== null && !Array.isArray(item)

/**
 * Statsig-wins-per-key merge, identical in semantics to
 * `mergeStatsigLocaleConfig` in statsig-provider.utils (kept inline here so this
 * foundational client module stays free of the Statsig SDK import graph):
 *   - a key present in `statsigConfig` wins (even if its value is undefined/null)
 *   - a key absent in `statsigConfig` is backfilled from `defaultConfig`
 *   - nested plain objects are merged recursively
 */
const mergeLocaleConfig = (
  defaultConfig: Record<string, unknown>,
  statsigConfig: Record<string, unknown>,
): Record<string, unknown> => {
  const result = { ...statsigConfig }
  for (const key of Object.keys(defaultConfig)) {
    if (key in statsigConfig) {
      if (isMergeableObject(defaultConfig[key]) && isMergeableObject(statsigConfig[key])) {
        result[key] = mergeLocaleConfig(defaultConfig[key], statsigConfig[key])
      }
      continue
    }
    result[key] = defaultConfig[key]
  }
  return result
}

// Default: static config as fallback before hydration. Initialised lazily on
// first read (NOT at module load) so importing this module is side-effect-free.
// `getLocaleConfig` does locale parsing (`toNormalisedLocale` etc.); running it at
// import time forced every test that transitively imports this module to mock the
// full `locale-config.constants` surface or crash on load. Behaviour is identical:
// the same static default is returned until hydrate/setLocaleConfigInMemory overwrite it.
let localeConfigMemory: StatsigLocaleDynamicConfig | null = null

function resolveDefaultLocaleConfig(): StatsigLocaleDynamicConfig {
  return getLocaleConfig(typeof window !== 'undefined' ? (getCookie(LOCALE_COOKIE) ?? '') : '')
}

/**
 * Hydrates from `__NEXT_DATA__.props.pageProps.localeConfig` — the
 * Statsig-resolved locale config injected by SSR. Called once at page load.
 *
 * The SSR config is merged OVER the current local default (not assigned
 * wholesale) using the same `mergeDefaultIntoStatsigLocaleConfig` rules as the
 * server and the React hook: the SSR value wins per key, and any key the SSR
 * snapshot is missing is backfilled from the current local default. This keeps
 * the non-React memory path consistent with `useStatsigDynamicLocaleConfig` and
 * guards against version skew (an older SSR bundle whose `localeConfig` predates
 * a key the current client bundle added — e.g. `isGa4Allowed`).
 */
export function hydrateLocaleConfigFromNextData(): void {
  if (typeof globalThis.window === 'undefined') return

  const nextData = (
    globalThis as typeof globalThis & {
      __NEXT_DATA__?: { props?: { pageProps?: { localeConfig?: StatsigLocaleDynamicConfig } } }
    }
  ).__NEXT_DATA__

  const config = nextData?.props?.pageProps?.localeConfig
  if (config && typeof config === 'object') {
    const defaultConfig = getLocaleConfig(getCookie(LOCALE_COOKIE) ?? '')
    localeConfigMemory = mergeLocaleConfig(
      defaultConfig as unknown as Record<string, unknown>,
      config as unknown as Record<string, unknown>,
    ) as unknown as StatsigLocaleDynamicConfig
  }
}

export function setLocaleConfigInMemory(config: StatsigLocaleDynamicConfig): void {
  localeConfigMemory = config
}

/**
 * Returns the full Statsig-resolved locale config.
 * Use this in non-React contexts for locale-specific config values.
 */
export function getLocaleConfigFromMemory(): StatsigLocaleDynamicConfig {
  localeConfigMemory ??= resolveDefaultLocaleConfig()
  return localeConfigMemory
}

/**
 * Single client-side source of truth for "is this a countryCompliant (UK/GDPR)
 * locale". Reads the `countryCompliant` key directly from the Statsig-resolved
 * locale config in memory — no separate `isCountryCompliant` feature-gate.
 * The pre-hydration fallback is the locale's static default, so it's locale-correct
 * (UK → true) before hydration too.
 */
export function getIsCountryCompliant(): boolean {
  return getLocaleConfigFromMemory().countryCompliant ?? false
}
